import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Divider,
  Grid,
  HStack,
  ModalCloseButton,
  Text,
} from '@chakra-ui/react';
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { delay, noop } from 'lodash';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { getI18n } from 'react-i18next';

import { ButtonSpinner } from '../LoadingSpinner';

interface IAlertDialog {
  onSubmit: () => Promise<any>;
  onCancel?: () => void;
  onClose?: () => void;
  isLoading?: boolean;
  redSubmitBttn?: boolean;
  catOrange?: boolean;
  redCancelBttn?: boolean;
  closeOnOverlayClick?: boolean;
  content?: {
    hide?: boolean;
    icon?: string | ReactElement;
    header?: string | ReactElement;
    //description?: string | ReactElement;
    footer?: {
      buttons: {
        hide?: boolean;
        cancelCaption?: string;
        actionCaption?: string;
        showCloseOnly?: boolean;
      };
    };
  };
  styleConfig?: {
    variant?: string;
    minW?: string;
    maxW?: string;
    motionPreset?: any;
    w?: string;
  };
  modal?: any;
  children: any;
  isUsedInPolling?: boolean;
}

export const AlertScaffolding = (props: IAlertDialog) => {
  const {
    styleConfig = {},
    content = {},
    isLoading = false,
    onSubmit,
    onCancel = false,
    onClose: onModalClose = false,
    children,
    closeOnOverlayClick = true,
    isUsedInPolling = false,
    modal,
    redSubmitBttn,
    redCancelBttn,
    catOrange,
  } = props;

  const { w = 'full', maxW = '50rem', minW, variant = false, motionPreset = 'slideInBottom' } = styleConfig;

  const { hide = false, icon = false, header = false, /*description=false,*/ footer = { buttons: {} } } = content;

  const {
    buttons: { hide: hideButtons = false, cancelCaption = false, actionCaption = getI18n().t('common:submit'), showCloseOnly = false },
  } = footer;

  const [disableClick, setDisableClick] = useState(false);
  useEffect(() => {
    if (disableClick) {
      delay(() => {
        setDisableClick(false);
      }, 3000);
    }
  }, [disableClick]);

  const [errors, setErrors] = useState(false);

  const cancelRef = useRef<HTMLButtonElement>(null);
  const onClose = () => {
    //sometimes only on modal close we want the user to be redirected
    if (onModalClose) {
      onModalClose();
    }
    if (!isUsedInPolling) {
      modal.hide();
      modal.remove();
    }
  };
  return (
    <AlertDialog
      onClose={onClose}
      isOpen={modal.visible}
      leastDestructiveRef={cancelRef}
      closeOnOverlayClick={closeOnOverlayClick}
      motionPreset={motionPreset}
    >
      <AlertDialogOverlay sx={{ backdropFilter: 'blur(2rem)' }}>
        <AlertDialogContent minW={minW} w={w} maxW={maxW}>
          {!hide ? (
            <AlertDialogHeader>
              <HStack>
                {icon}
                {header ? (
                  <Box px={3} fontWeight={'bold'} fontSize="md">
                    {header}
                  </Box>
                ) : null}
              </HStack>
            </AlertDialogHeader>
          ) : null}

          {closeOnOverlayClick ? <ModalCloseButton /> : null}
          {!hide ? <Divider /> : null}
          <AlertDialogBody ml={'12px'}>
            {children}
            <Grid>
              <Box fontSize={'xs'}>
                <Text fontSize="sm" color={'error'}>
                  {errors}
                </Text>
              </Box>
            </Grid>
          </AlertDialogBody>
          {!hideButtons && !hide ? (
            <AlertDialogFooter>
              {catOrange ? (
                <Grid templateColumns={`repeat(${cancelCaption ? '2' : '1'}, 1fr)`} gap={3}>
                  {cancelCaption && (
                    <Button
                      bgColor={'white'}
                      color={'red.500'}
                      border="1px"
                      variant="primary"
                      _hover={{
                        bgColor: 'red.500',
                        borderColor: 'red.500',
                        border: '1px',
                        color: 'white',
                      }}
                      ref={cancelRef}
                      onClick={() => {
                        onClose();
                        if (onCancel) {
                          onCancel();
                        }
                      }}
                      disabled={isLoading || disableClick}
                    >
                      {cancelCaption}
                    </Button>
                  )}
                  <Button
                    bgColor={'orange.500'}
                    variant="primary"
                    color="on-accent-muted"
                    border="1px"
                    _hover={{
                      bgColor: 'white',
                      borderColor: 'orange.500',
                      border: '1px',
                      color: 'orange.500',
                    }}
                    onClick={() => {
                      setDisableClick(true);
                      Promise.resolve(onSubmit())
                        .catch((error) => {
                          setErrors(error);
                          throw Error(error);
                        })
                        .then(() => (!errors ? onClose() : noop));
                    }}
                    disabled={isLoading || disableClick}
                  >
                    {actionCaption} {isLoading ? <ButtonSpinner /> : null}
                  </Button>
                </Grid>
              ) : (
                <Grid templateColumns={`repeat(${cancelCaption ? '2' : '1'}, 1fr)`} gap={3}>
                  {cancelCaption && (
                    <Button
                      variant={variant ? `outline-${variant}` : 'outline'}
                      color={redCancelBttn ? 'red' : 'inherit'}
                      _hover={{ bg: redCancelBttn ? 'white' : 'inherit' }}
                      ref={cancelRef}
                      onClick={() => {
                        onClose();
                        if (onCancel) {
                          onCancel();
                        }
                      }}
                      disabled={isLoading || disableClick}
                    >
                      {cancelCaption}
                    </Button>
                  )}
                  <Button
                    variant={redSubmitBttn ? undefined : 'primary'}
                    colorScheme="red"
                    onClick={() => {
                      if (showCloseOnly) {
                        onClose();
                      } else {
                        setDisableClick(true);
                        Promise.resolve(onSubmit())
                          .catch((error) => {
                            setErrors(error);
                            throw Error(error);
                          })
                          .then(() => (!errors ? onClose() : noop));
                      }
                    }}
                    disabled={isLoading || disableClick}
                  >
                    {actionCaption} {isLoading ? <ButtonSpinner /> : null}
                  </Button>
                </Grid>
              )}
            </AlertDialogFooter>
          ) : null}
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  );
};

export default NiceModal.create((props: IAlertDialog) => {
  const modal = useModal();
  return <AlertScaffolding {...props} modal={modal} />;
});
